import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { router } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Save, Send, Mail } from 'lucide-react';
import { Link } from '@inertiajs/react';
import { RichTextEditor } from '@/components/RichTextEditor';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

type MatchStatus = 'all' | 'unmatched' | 'matched'
interface Campaign {
  id?: number;
  subject?: string;
  body_html?: string;
  recipient_filter?: {
    roles?: string[];
    categories?: string[];
    match_status?: MatchStatus;
  };
}

interface Props {
  campaign?: Campaign;
  available_variables: string[];
  errors?: string[];
  mode?: 'new' | 'edit';
}

interface FormData {
  subject: string;
  body_html: string;
  recipient_filter: {
    roles?: string[];
    categories?: string[];
    match_status?: MatchStatus;
  };
}

export default function CampaignForm({
  campaign,
  available_variables,
  errors,
  mode = campaign?.id ? 'edit' : 'new',
}: Props) {
  const isEdit = mode === 'edit';
  const { register, handleSubmit, watch, setValue } = useForm<FormData>({
    defaultValues: {
      subject: campaign?.subject || '',
      body_html: campaign?.body_html || '',
      recipient_filter: campaign?.recipient_filter || {
        roles: [],
        categories: [],
        match_status: 'all'
      },
    },
  });

  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    campaign?.recipient_filter?.roles || []
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    campaign?.recipient_filter?.categories || []
  );

  const [selectedMatchStatus, setSelectedMatchStatus] = useState<MatchStatus>(campaign?.recipient_filter?.match_status || 'all')
  const [isSubmitting, setIsSubmitting] = useState(false);

  const processEmailPreview = (content: string): string => {
    if (!content) return '';

    // Replace variables with sample data
    return content
      .replace(/\{\{first_name\}\}/g, 'John')
      .replace(/\{\{last_name\}\}/g, 'Doe')
      .replace(/\{\{role\}\}/g, 'Mentor')
      .replace(/\{\{category\}\}/g, 'Pre-health')
      .replace(/\{\{email\}\}/g, 'john.doe@example.com');
  };

  const handleRoleChange = (role: string, checked: boolean) => {
    const updated = checked
      ? [...selectedRoles, role]
      : selectedRoles.filter((r) => r !== role);
    setSelectedRoles(updated);
    setValue('recipient_filter.roles', updated);
  };

  const handleCategoryChange = (category: string, checked: boolean) => {
    const updated = checked
      ? [...selectedCategories, category]
      : selectedCategories.filter((c) => c !== category);
    setSelectedCategories(updated);
    setValue('recipient_filter.categories', updated);
  };

  const handleMatchStatusChange = (status: MatchStatus) => {
    setSelectedMatchStatus(status)
    setValue('recipient_filter.match_status', status)
  }

  const onSubmit = (data: FormData) => {
    setIsSubmitting(true);

    const campaignData = {
      campaign: {
        ...data,
        recipient_filter: {
          roles: selectedRoles.length > 0 ? selectedRoles : undefined,
          categories:
            selectedCategories.length > 0 ? selectedCategories : undefined,
          match_status: selectedMatchStatus
        },
      },
    };


    if (isEdit && campaign?.id) {
      // Update existing campaign
      router.patch(`/admin/campaigns/${campaign.id}`, campaignData, {
        onFinish: () => setIsSubmitting(false),
      });
    } else {
      // Create new campaign
      router.post('/admin/campaigns', campaignData, {
        onFinish: () => setIsSubmitting(false),
      });
    }
  };

  const handleTestEmail = () => {
    const data = watch();
    router.post(
      '/admin/campaigns/send_test',
      {
        campaign: { ...data },
      },
      {
        onSuccess: () => {
          toast.success('Test email sent successfully!');
        },
      }
    );
  };

  return (
    <AdminLayout title={isEdit ? 'Edit Campaign' : 'New Campaign'} breadcrumbs={[{ label: 'Campaigns', href: '/admin/campaigns' }]}>
      <div className="min-h-screen">
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <Link href="/admin/campaigns">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="hover:bg-gray-100"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </Link>
                <div className="">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {isEdit ? 'Edit Campaign' : 'New Email Campaign'}
                  </h1>
                  <p className="text-gray-600 mt-1">
                    {isEdit
                      ? 'Update your email campaign'
                      : 'Create and send emails to your users'}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestEmail}
                  disabled={!watch('subject') || !watch('body_html')}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Send Test
                </Button>
                <Button
                  type="submit"
                  form="campaign-form"
                  disabled={isSubmitting}
                >
                  {isEdit ? (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      {isSubmitting ? 'Creating...' : 'Create Campaign'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <form
            id="campaign-form"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6"
          >
            {errors && errors.length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  <ul className="list-disc list-inside">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 space-y-6">
                <Card className="border-0 shadow-sm pt-0 pb-6">
                  <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                    <CardTitle className="text-lg font-medium">Email Content</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-6">
                    <div>
                      <Label htmlFor="subject" className="text-sm font-medium text-gray-700">
                        Subject Line
                      </Label>
                      <Input
                        id="subject"
                        placeholder="Enter email subject..."
                        className="mt-2"
                        {...register('subject', { required: true })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="body" className="text-sm font-medium text-gray-700 block mb-2">
                        Email Body
                      </Label>
                      <RichTextEditor
                        content={watch('body_html') || ''}
                        onChange={(html) => setValue('body_html', html)}
                        placeholder="Write your email content here..."
                        availableVariables={available_variables}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Email Preview */}
                <Card className="border-0 shadow-sm pt-0 pb-6">
                  <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                    <div>
                      <CardTitle className="text-lg font-medium">Email Preview</CardTitle>
                      <p className="text-sm text-gray-500 mt-1">
                        Using sample data for variables
                      </p>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="bg-white p-6 space-y-4">
                      <div className="pb-3 border-b border-gray-100">
                        <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">
                          From
                        </p>
                        <p className="text-sm text-gray-700">
                          Project Short &lt;noreply@project-short.com&gt;
                        </p>
                      </div>
                      <div className="pb-3 border-b border-gray-100">
                        <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">
                          Subject
                        </p>
                        <p className="font-medium text-gray-900">
                          {watch('subject') || 'No subject'}
                        </p>
                      </div>
                      <div className="pt-2">
                        <div
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{
                            __html:
                              processEmailPreview(watch('body_html')) ||
                              '<p class="text-gray-400 italic">Email content will appear here...</p>',
                          }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="lg:w-80">
                <Card className="border-0 shadow-sm lg:sticky lg:top-6 pt-0 pb-6">
                  <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                    <CardTitle className="text-lg font-medium">Recipients</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-6">
                    <div className='flex flex-col gap-3'>
                      <Label className="text-xs uppercase tracking-wider text-gray-500 font-medium">Roles</Label>
                      <div className="space-y-2">
                        {['mentor', 'mentee'].map((role) => (
                          <div key={role} className="flex items-center space-x-2">
                            <Checkbox
                              id={`role-${role}`}
                              checked={selectedRoles.includes(role)}
                              onCheckedChange={(checked) =>
                                handleRoleChange(role, checked as boolean)
                              }
                            />
                            <Label
                              htmlFor={`role-${role}`}
                              className="text-sm font-normal capitalize cursor-pointer"
                            >
                              {role}s
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className='flex flex-col gap-3'>
                      <Label className="text-xs uppercase tracking-wider text-gray-500 font-medium">Categories</Label>
                      <div className="space-y-2">
                        {['pre-health', 'graduate'].map((category) => (
                          <div
                            key={category}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`category-${category}`}
                              checked={selectedCategories.includes(category)}
                              onCheckedChange={(checked) =>
                                handleCategoryChange(category, checked as boolean)
                              }
                            />
                            <Label
                              htmlFor={`category-${category}`}
                              className="text-sm font-normal cursor-pointer"
                            >
                              {category === 'pre-health'
                                ? 'Pre-Health'
                                : 'Graduate'}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className='flex flex-col gap-3'>
                      <Label className='text-xs uppercase tracking-wider text-gray-500 font-medium'>Match Status</Label>
                      <RadioGroup defaultValue={selectedMatchStatus}
                        onValueChange={(value: MatchStatus) => handleMatchStatusChange(value)}>
                        <div className='flex items-center gap-3'>
                          <RadioGroupItem value="all" id="match-all" />
                          <Label htmlFor='match-all'>All</Label>
                        </div>
                        <div className='flex items-center gap-3'>
                          <RadioGroupItem value='unmatched' id="match-unmatched" />
                          <Label htmlFor='match-unmatched'>Unmatched</Label>
                        </div>
                        <div className='flex items-center gap-3'>
                          <RadioGroupItem value='matched' id="match-matched" />
                          <Label htmlFor='match-matched'>Matched</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {selectedRoles.length === 0 &&
                      selectedCategories.length === 0 && (
                        <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">All verified users</span> will receive this email
                          </p>
                        </div>
                      )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}
